import axios from 'axios';
import { LoginRequest, RegisterRequest, AuthResponse } from '../types/auth';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: '/api',
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const confirmClient = axios.create({
  baseURL: '/server-confirm',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      window.location.href = '/sso/login';
    }
    return Promise.reject(error);
  }
);

confirmClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const authService = {
  // Login user
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await apiClient.post('/auth/login', credentials);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || '登录失败，请稍后再试。',
      };
    }
  },

  // Register user
  async register(userData: RegisterRequest): Promise<AuthResponse> {
    try {
      const response = await apiClient.post('/auth/register', {
        username: userData.username,
        email: userData.email,
        password: userData.password,
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || '注册失败，请稍后再试。',
      };
    }
  },

  // Logout user
  async logout(): Promise<AuthResponse> {
    try {
      const response = await apiClient.post('/auth/logout');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || '退出失败。',
      };
    }
  },

  // Reset password
  async resetPassword(email: string): Promise<AuthResponse> {
    try {
      const response = await apiClient.post('/auth/reset-password', { email });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || '重置请求失败，请稍后再试。',
      };
    }
  },

  // Get current user
  async getCurrentUser(): Promise<AuthResponse> {
    try {
      const response = await apiClient.get('/auth/me');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || '获取用户信息失败。',
      };
    }
  },

  // Change password with token
  async changePassword(token: string, newPassword: string): Promise<AuthResponse> {
    try {
      const response = await apiClient.post('/auth/change-password', {
        token,
        newPassword,
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || '修改密码失败。',
      };
    }
  },

  async confirmResetPassword(id: string, password: string, confirmPassword: string): Promise<AuthResponse> {
    try {
      const response = await confirmClient.post('/reset-password', {
        id,
        password,
        confirmPassword,
      });
      if (response.data?.code === 0) {
        return { success: true, message: response.data?.msg || '密码重置成功' };
      }
      return {
        success: false,
        error: response.data?.msg || '密码重置失败。',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.msg || '密码重置失败。',
      };
    }
  },

  async sendResetEmailByEmail(email: string): Promise<AuthResponse> {
    try {
      const response = await confirmClient.post('/reset-email-by-email', { email });
      if (response.data?.code === 0) {
        return { success: true, message: response.data?.msg || '重置邮件已发送' };
      }
      return {
        success: false,
        error: response.data?.msg || '发送重置邮件失败。',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.msg || '发送重置邮件失败。',
      };
    }
  },
};

export default authService;
